import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Divider, Flex, Form, Input, Modal, Steps } from "antd";
import Upload from "@/components/Upload";
import templateStore from "@/store/templateStore";
import { GetLocalStorage } from "@/utils/helper";
import PublicPet from "@/page/PublicTemplates/Pet";
import type { ICusTemplate } from "@/store/interface/template";
import { validatePhoneNumber } from "@/utils/validate";

const FormPet = ({ initialData }: { initialData?: ICusTemplate }) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewConfig, setPreviewConfig] = useState<any>({});
  const { saveConfig, updateConfig } = templateStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (initialData) form.setFieldsValue(initialData.config);
  }, []);

  const handlePreview = () => {
    setPreviewConfig(form.getFieldsValue());
    setPreviewOpen(true);
  };

  const steps = [{ title: "Bước 1" }, { title: "Bước 2" }, { title: "Bước 3" }];

  const stepContent = [
    // Step 1 — Thông tin thú cưng
    <>
      <Form.Item
        name="pet_avatar"
        label="Ảnh đại diện thú cưng"
        rules={[{ required: true, message: "Vui lòng chọn ảnh" }]}
      >
        <Upload maxSize={10} />
      </Form.Item>
      <Form.Item name="pet_name" label="Tên thú cưng" rules={[{ required: true, message: "Vui lòng nhập tên" }]}>
        <Input placeholder="VD: Bông, Milo, Lucky..." />
      </Form.Item>
      <Form.Item name="pet_type" label="Loại thú cưng">
        <Input placeholder="VD: Chó, Mèo, Hamster, Thỏ..." />
      </Form.Item>
      <Form.Item name="breed" label="Giống">
        <Input placeholder="VD: Corgi, Poodle, Munchkin, British Shorthair..." />
      </Form.Item>
      <Form.Item name="gender" label="Giới tính">
        <Input placeholder="Đực / Cái" />
      </Form.Item>
      <Form.Item name="birthday" label="Ngày sinh / Tuổi">
        <Input placeholder="VD: 15/03/2023 hoặc 2 tuổi" />
      </Form.Item>
      <Form.Item name="cover_image" label="Ảnh bìa">
        <Upload maxSize={10} />
      </Form.Item>
    </>,
    // Step 2 — Tính cách & thông tin bổ sung
    <>
      <Form.Item name="bio" label="Giới thiệu">
        <Input.TextArea placeholder="Mô tả tính cách, sở thích của bé..." rows={4} />
      </Form.Item>
      <Form.Item name="personality" label="Tính cách nổi bật">
        <Input placeholder="VD: Hiền lành, Nghịch ngợm, Thích ôm ấp..." />
      </Form.Item>
      <Form.Item name="favorite_food" label="Món ăn yêu thích">
        <Input placeholder="VD: Cá hồi, Gà luộc, Hạt Royal Canin..." />
      </Form.Item>
      <Form.Item name="favorite_activity" label="Hoạt động yêu thích">
        <Input placeholder="VD: Đuổi bóng, Nằm phơi nắng, Chạy nhảy..." />
      </Form.Item>
      <Form.Item name="weight" label="Cân nặng">
        <Input placeholder="VD: 3.5 kg" />
      </Form.Item>
      <Form.Item name="color" label="Màu lông">
        <Input placeholder="VD: Trắng vàng, Đen tuyền, Tam thể..." />
      </Form.Item>

      <Divider plain style={{ fontSize: "0.8rem", color: "rgba(0,0,0,0.4)" }}>
        👤 Thông tin chủ nhân
      </Divider>

      <Form.Item name="owner_name" label="Tên chủ nhân">
        <Input placeholder="Nhập tên của bạn" />
      </Form.Item>
      <Form.Item
        name="owner_phone"
        label="Số điện thoại (phòng khi bé lạc)"
        rules={[
          () => ({
            validator(_, value) {
              if (!value || validatePhoneNumber(value)) {
                return Promise.resolve();
              }
              return Promise.reject(new Error("Số điện thoại không hợp lệ"));
            },
          }),
        ]}
      >
        <Input placeholder="Nhập số điện thoại" />
      </Form.Item>
      <Form.Item name="owner_address" label="Địa chỉ">
        <Input placeholder="Nhập địa chỉ" />
      </Form.Item>
    </>,
    // Step 3 — Ảnh & Video
    <>
      <Form.Item name="video" label="Video thú cưng">
        <Upload type="video" maxSize={10} />
      </Form.Item>
      <Form.Item name="gallery" label="Bộ ảnh thú cưng">
        <Upload multiple maxSize={10} />
      </Form.Item>
    </>,
  ];

  const next = () => setCurrentStep(currentStep + 1);
  const prev = () => setCurrentStep(currentStep - 1);

  const handleFinish = async (value: any) => {
    try {
      let result;
      if (initialData) {
        result = await updateConfig(initialData.xid, { config: { ...value } });
      } else {
        const serial = await GetLocalStorage("scan-storage", "serial");
        result = await saveConfig({ type: "pet", chip_serial: serial, config: { ...value } });
      }
      if (result) navigate("/");
    } catch (error) {
      console.log("error", error);
    }
  };

  return (
    <section className="container">
      <Steps responsive={false} type="dot" titlePlacement="vertical" current={currentStep} items={steps} />

      <Form form={form} layout="vertical" style={{ marginTop: 24 }} onFinish={handleFinish} preserve>
        {stepContent.map((content, index) => (
          <div key={index} style={{ display: index === currentStep ? "block" : "none" }}>
            {content}
          </div>
        ))}

        <Flex className="mt-3">
          {currentStep > 0 && (
            <Button style={{ marginRight: 8 }} onClick={prev} className="w-full">
              Quay lại
            </Button>
          )}
          {currentStep < steps.length - 1 && (
            <Button type="primary" onClick={next} className="w-full">
              Tiếp theo
            </Button>
          )}
          {currentStep === steps.length - 1 && (
            <>
              <Button onClick={handlePreview} style={{ marginRight: 8 }} className="w-full">
                Xem trước
              </Button>
              <Button type="primary" htmlType="submit" className="w-full">
                Hoàn thành
              </Button>
            </>
          )}
        </Flex>
      </Form>

      <Modal
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        title="Xem trước"
        width={480}
        style={{ top: 8 }}
        styles={{ body: { padding: 0, height: "calc(100vh - 110px)", overflow: "auto" } }}
      >
        <PublicPet config={previewConfig} />
      </Modal>
    </section>
  );
};

export default FormPet;
